/**
 * What `/fly`'s boot is made of, as a phase list with **measured** durations.
 *
 * A progress bar is a claim about how much of the wait is left, and the only
 * honest way to make that claim is to measure. `BOOT_PHASE_MS` is milliseconds
 * off real boots of this application — the runs and the conditions are tabulated
 * in `docs/engineering/fly-boot-timing.md` — and everything else here is derived
 * from them. Nothing in this module animates on a clock of its own invention.
 *
 * ## Why a phase needs a duration and not just a share
 *
 * The phases are wildly uneven and their unevenness *moves with the network*:
 * on a warm cache the airframe is 30 ms and on a cold 20 Mbit link it is 730 ms,
 * while the pipeline precompile is 570 ms in every condition measured because it
 * is compute. A bar segmented by share alone would therefore sit still for
 * two-thirds of a cold boot and then leap. So each phase carries an **expected
 * duration**, and the bar eases across that phase's own share on the curve
 * `1 − e^(−k·t/expected)` — which approaches the phase's end without ever
 * reaching it, so a phase that runs long can never claim to have finished, and
 * the boundary is only crossed when the boot really does move on.
 *
 * ## Why these numbers and not the warm ones
 *
 * They are taken from the **cold** 20 Mbit profile, averaged over the ENHANCED
 * MODELS on and off runs, because that is the boot a loading screen exists for:
 * a warm boot is over in 1.3 s and the screen is a flicker either way. The two
 * runs disagree most about `airframe` and `region` — with the boot's fetches now
 * running concurrently (`scene-boot.ts`), whichever of them the thread is inside
 * when the region's synchronous build lands absorbs it — so their shares are the
 * mean of the two rather than either one, and no phase is left at zero.
 *
 * Nothing here imports three.js, so the phase model is testable without a GPU.
 */

/**
 * One step of `scene-boot.ts`'s `watchInit`, reported as it *starts* — so the
 * label always names what the boot is now waiting for.
 *
 * `device` covers `renderer.init()` and the first viewport sizing; `guns` the
 * cockpit's muzzle table; `airframe` the player's own aeroplane (the enhanced
 * `.glb` when ENHANCED MODELS is on — the single largest fetch a boot makes);
 * `propellers` AD-012's discs; `region` the `.WLD` body, its ~50 placed models,
 * the relief field and the instanced scenery; `terrain` draping that relief into
 * the scene; and `precompile` every WGSL pipeline, so the first drawn frame does
 * not compile one on the main thread (`three/precompile-scene.ts`).
 *
 * The middle three are *started together* and only awaited in this order, so a
 * label here names the outstanding wait rather than the exclusive occupant of
 * the thread. That is deliberate and it is what made the cold boot 11 % shorter.
 */
export type BootPhase = "device" | "guns" | "airframe" | "propellers" | "region" | "terrain" | "precompile";

/** The phases in the order `watchInit` reports them. */
export const BOOT_PHASES: readonly BootPhase[] = ["device", "guns", "airframe", "propellers", "region", "terrain", "precompile"];

/**
 * Milliseconds each phase is expected to take, from the reference cold 20 Mbit
 * runs. Used for two different things — the *share* of the bar a phase owns, and
 * the rate the bar eases across it — which is why they are durations rather than
 * normalised weights.
 *
 * `propellers` and `terrain` are genuinely 1 ms once `BARON.PAL` is in hand and
 * are recorded as 5 rather than padded to something "visible": a step that costs
 * nothing should take none of the bar. They are floored at 5 only so no expected
 * duration is zero, which the easing divides by.
 */
export const BOOT_PHASE_MS: Readonly<Record<BootPhase, number>> = {
  device: 10,
  guns: 430,
  airframe: 430,
  propellers: 5,
  region: 220,
  terrain: 5,
  precompile: 580,
};

const TOTAL_MS = BOOT_PHASES.reduce((sum, phase) => sum + BOOT_PHASE_MS[phase], 0);

/**
 * How fast the bar eases across a phase's share. At `t = expected` the segment
 * is `1 − e^−2` = 86 % filled, so a phase that runs to its measured length looks
 * nearly done and one that overruns keeps creeping instead of stopping dead.
 */
const EASE_RATE = 2;

/**
 * The most of a segment easing may ever claim.
 *
 * `1 − e^(−k·t)` is asymptotic in arithmetic and **not** in floating point: past
 * about 20 time constants `e^(−k·t)` underflows to zero and the expression
 * returns exactly 1, which would put the bar hard against the next boundary —
 * and for the last phase, hard against 100 % — while the screen is still black.
 * A test caught it at ten minutes inside a phase. So the easing is capped: the
 * final 1 % of a segment is never claimed by the clock, only by the boot
 * actually moving on.
 */
const EASE_CAP = 0.99;

/**
 * How much of a phase's segment its own **measurement** may claim, per phase.
 *
 * A phase is some measurable work plus whatever follows it, and the share is
 * how much of the phase the measurable half actually is. Both numbers are
 * measured rather than chosen:
 *
 * - `airframe` reports **bytes**, and a 1.4 MB `.glb` is followed by a
 *   `GLTFLoader` parse, a repaint and a scene mutation that the byte count says
 *   nothing about — so a fully-arrived download moves the bar four fifths of
 *   the phase and no further.
 * - `precompile` reports **drawables compiled**, and the phase is that loop
 *   plus `restoreSceneFlags`, which is a flag write per node and lands under a
 *   millisecond against the loop's hundreds. So its measurement covers almost
 *   all of it, and the share says so.
 *
 * Every share is below `EASE_CAP`, which keeps the one invariant this module
 * has: nothing but the next phase starting may cross a phase boundary.
 */
const MEASURED_SHARE: Readonly<Partial<Record<BootPhase, number>>> = {
  airframe: 0.8,
  precompile: 0.97,
};

/** The share for a phase with no entry above — the conservative one. */
const DEFAULT_MEASURED_SHARE = 0.8;

/**
 * How far the easing may run **while a measurement is in hand**.
 *
 * This is the number that fixes "a full bar and a ticking second-counter for
 * the majority of the wait" (user, 2026-08-31). The easing is a guess
 * calibrated on a mean, and it reaches the top of its segment after about five
 * time constants; a phase that runs ten times its expectation therefore parked
 * the bar against the boundary and *stayed there*, and it did so even after the
 * phase learned to measure itself, because `Math.max` handed the segment to
 * whichever channel was further along and the saturated curve always was.
 *
 * So where there is a measurement, the curve is only allowed to cover the
 * opening of the segment — enough that a fast phase still animates smoothly
 * from its own boundary — and everything past that has to be *earned* by work
 * actually reported. A `precompile` that takes a minute now crawls its segment
 * in step with the pipelines it has really compiled instead of claiming the lot
 * in the first two seconds.
 *
 * An eighth, and the number is the answer to "how long may the bar sit still at
 * the start of a phase that is measuring itself?" — because until the phase's
 * own measurement passes the curve, the curve is what the bar shows. Traced on
 * the real canvas through a 20×-CPU-throttled boot, where `precompile` runs
 * 8.2 s: at a third the bar held its opening position for **3.5 s** before the
 * measurement caught up, and at an eighth for **1.3 s**. Lower still would start
 * the segment with a visible stall on a *healthy* boot, where the first chunk
 * has not landed yet and the curve is the only thing there is to draw.
 */
const EASE_CAP_MEASURED = 1 / 8;

/**
 * Which phases have their measurement in hand from their **own first report**,
 * and therefore may have the curve held back to `EASE_CAP_MEASURED`.
 *
 * The distinction is what keeps the bar monotonic, and it is not cosmetic.
 * `precompile` reports `(0, total)` before its first chunk, because it knows the
 * whole of its own work the moment the phase begins — so the uncapped curve
 * never runs for it and capping the curve can never pull the bar back.
 * `airframe` is the opposite: its total is the `.glb`'s decoded length, which
 * arrives from the manifest partway through a phase that has already been easing
 * for a while. Capping the curve *there* would drop the bar backwards on the
 * frame the first byte count landed — the one thing this module may never do —
 * so the airframe keeps exactly the arrangement G58 shipped: the full easing,
 * and bytes taking over wherever they have run further.
 */
const MEASURES_FROM_START: ReadonlySet<BootPhase> = new Set<BootPhase>(["precompile"]);

/**
 * How far past its expected duration a phase runs before the screen starts
 * saying so, as a multiple of the expectation.
 *
 * Two, because one is the *mean* — half of all healthy boots are slower than
 * their expected duration by construction, and a caption that appeared on half
 * of all boots would be noise rather than information.
 */
export const BOOT_OVERRUN_FACTOR = 2;

/**
 * The floor under that threshold, and the reason there is one: `propellers` and
 * `terrain` expect 5 ms, so twice their expectation is 10 ms and the caption
 * would fire on every boot ever taken. Four seconds is the point at which a
 * wait stops reading as a load and starts reading as a hang, which is exactly
 * when the screen owes the player an explanation.
 */
export const BOOT_OVERRUN_FLOOR_MS = 4_000;

/**
 * How much of the current phase's own measurable work is finished.
 *
 * Deliberately unitless: `airframe` counts the decoded bytes of the enhanced
 * `.glb` off the wire and `precompile` counts the drawables it has compiled
 * pipelines for. What the two have in common — and the only property the bar
 * needs — is that `done` never decreases within a phase and `total` is the
 * whole of what that phase will report.
 *
 * `total` of zero means **no signal**, which is a different thing from zero
 * progress and is why this is not just a fraction: a phase that cannot measure
 * itself must fall back to the easing rather than pin the bar at its own
 * boundary (G58's lesson, met once already in `three/streamed-body.ts`).
 */
export interface BootMeasure {
  readonly done: number;
  readonly total: number;
}

/**
 * The share of the whole boot that is complete when `phase` is **about to
 * start** — the sum of every earlier phase's expected duration, normalised.
 *
 * `bootProgressAt("device")` is therefore 0, and the bar is full only when the
 * scene reports `ready`, which is a separate signal rather than an eighth phase:
 * a bar that reaches 100 % while the picture is still black is the lie this
 * module exists to avoid.
 */
export function bootProgressAt(phase: BootPhase): number {
  let done = 0;
  for (const candidate of BOOT_PHASES) {
    if (candidate === phase) break;
    done += BOOT_PHASE_MS[candidate];
  }
  return done / TOTAL_MS;
}

/** The share of the bar `phase` owns. */
export function bootProgressSpan(phase: BootPhase): number {
  return BOOT_PHASE_MS[phase] / TOTAL_MS;
}

/**
 * Where the bar stands `elapsedMs` into `phase`, given whatever that phase has
 * managed to measure about itself.
 *
 * Strictly inside `[start, start + span)`: it starts at the phase's own boundary
 * and approaches the next one asymptotically, so however long a phase takes the
 * bar never crosses into the next phase's territory and never reads as complete.
 *
 * `measure` is the honest half, and it now **governs the segment** rather than
 * merely competing with the curve. The easing is a guess calibrated on a mean,
 * and on the boots where the guess is worst — a slow link where `airframe`'s
 * 1.4 MB takes seven seconds instead of four hundred milliseconds, a slow
 * adapter where `precompile` takes a minute instead of eight hundred
 * milliseconds — it saturates almost immediately and then says nothing for the
 * rest of the wait. So where a measurement exists the curve is held to
 * `EASE_CAP_MEASURED` and the rest of the segment has to be earned; where there
 * is none, the curve carries the phase to `EASE_CAP` as it always did.
 *
 * `Math.max` of the two is what keeps the bar monotonic across the moment a
 * measurement first appears: a phase that eased for a while and only then
 * learned its own total cannot jump backwards.
 */
export function bootBarFraction(phase: BootPhase, elapsedMs: number, measure?: BootMeasure): number {
  const start = bootProgressAt(phase);
  const span = bootProgressSpan(phase);
  const measuring = measure !== undefined && measure.total > 0;
  const t = Math.max(0, elapsedMs) / BOOT_PHASE_MS[phase];
  const capped = measuring && MEASURES_FROM_START.has(phase) ? EASE_CAP_MEASURED : EASE_CAP;
  const eased = span * Math.min(capped, 1 - Math.exp(-EASE_RATE * t));
  const share = MEASURED_SHARE[phase] ?? DEFAULT_MEASURED_SHARE;
  const measured = measuring ? span * share * Math.min(1, Math.max(0, measure.done / measure.total)) : 0;
  return start + Math.max(eased, measured);
}

/** The point past which `phase` has blown its budget and the caption starts explaining itself. */
export function bootOverrunAfterMs(phase: BootPhase): number {
  return Math.max(BOOT_PHASE_MS[phase] * BOOT_OVERRUN_FACTOR, BOOT_OVERRUN_FLOOR_MS);
}

/**
 * The port's own words for the port's phases. `RB.EXE` shows no per-phase
 * loading text — it paints a briefing and swaps to the cockpit — so none of
 * these is transcribed from a string table. Each one describes what that step
 * actually does, in the shell's telegraphic upper case: the guns line really is
 * the gun-mount table, and the survey line really is the sector's `.WLD`.
 */
export const BOOT_PHASE_LABELS: Readonly<Record<BootPhase, string>> = {
  device: "STARTING THE ENGINE",
  guns: "LOADING THE GUNS",
  airframe: "ROLLING OUT THE AIRCRAFT",
  propellers: "FITTING THE PROPELLER",
  region: "SURVEYING THE SECTOR",
  terrain: "MAPPING THE GROUND",
  precompile: "PRE-FLIGHT CHECKS",
};

/**
 * The caption under the heading: the phase's own words, and — once the phase has
 * run past `bootOverrunAfterMs` — how long it has been running.
 *
 * **The seconds are the whole point of this function.** By construction the bar
 * cannot move once a phase passes about five of its time constants: the easing
 * is capped at 99 % of the segment, so a phase that blows its budget parks a
 * static bar against a boundary and says nothing, which is exactly what "stuck
 * at 99 % for almost a minute" was (user report, 2026-08-26). A ticking count is
 * the honest thing to put there — it is real, it is motion, it names the step
 * that is taking the time, and it claims no progress that has not happened.
 *
 * Set in the shell's own telegraphic upper case, with a hyphen and an `S`
 * because both are in `FONT3` and neither needs a glyph the bake does not ship.
 */
export function bootPhaseCaption(phase: BootPhase, elapsedMs: number): string {
  const label = BOOT_PHASE_LABELS[phase];
  if (elapsedMs < bootOverrunAfterMs(phase)) return label;
  return `${label} - ${Math.floor(elapsedMs / 1000)}S`;
}

/** What the overlay is handed each time the boot moves on. */
export interface BootProgress {
  readonly phase: BootPhase;
  /** 0…1, the share of the boot already done when this phase started. */
  readonly fraction: number;
  readonly label: string;
  /**
   * The phase's own measurement of itself, where it has one — and a report
   * carrying it is a *re-report of the same phase*, not a new one.
   *
   * Two phases do. `airframe` counts the enhanced `.glb` off the wire, which is
   * the largest single thing a boot fetches (`docs/engineering/fly-boot-timing.md`
   * §6). `precompile` counts the drawables it has compiled, which is the phase
   * that dominates a boot once the bytes are local and the one the 2026-08-31
   * "preflight checks for a minute" report is about (§6e).
   */
  readonly measure?: BootMeasure;
  /**
   * A standing fact about this boot rather than a step in it — printed under the
   * bar and, unlike the phase caption, carried unchanged through every report
   * that follows the one which set it.
   *
   * There is exactly one today: `RENDERER: WEBGL` when the flight did not come
   * up on WebGPU (`three/renderer-mode.ts`). It is deliberately absent on the
   * ordinary path, so the loading screen a player sees every sortie is the one
   * it has always been and the line appears only when there is something to say.
   */
  readonly note?: string;
}

/** The report for a phase that is starting now, or a fresh measurement for the one already running. */
export function bootProgressFor(phase: BootPhase, measure?: BootMeasure, note?: string): BootProgress {
  return { phase, fraction: bootProgressAt(phase), label: BOOT_PHASE_LABELS[phase], measure, note };
}
